import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TablaPostulaciones from "../TablaPostulaciones";
import EditorPrefiltro from "./EditorPrefiltro";
import DetallePrefiltro, { TarjetaPrefiltro } from "./DetallePrefiltro";
import { fetchPrefilterSummary } from "./prefiltradoService";
import {
  IClientGroup,
  IPrefilter,
  IPrefilterSelection,
  IPrefilterSummaryResponse,
  groupByClient,
} from "./prefiltradoTypes";

// Vista mínima: solo un botón por cliente y, si el cliente tiene varias sedes,
// un menú que sale del botón para elegir la sede; debajo, la tabla con quienes
// cumplen. Las condiciones del prefiltro (comunas, edad, cargos, género y
// turno) solo se ven al pasar el mouse por el cliente o por cada sede; el único
// número visible es cuántos cumplen, junto a cada sede en el menú.
// Las versiones anteriores (tarjetas con conteos y condiciones; cabecera con
// recuadros por grupo) están en la rama local `cyber-89-prefiltrado-v1-tarjetas`
// y en el historial de `feature/cyber-89-prefiltrado`.

const FONT = "Roboto, sans-serif";
const C = {
  blue: "#0065DD",
  blueBg: "rgba(0,101,221,0.08)",
  slate: "#64748b",
  red: "#b91c1c",
  redBg: "rgba(239,68,68,0.10)",
  ink: "#1e293b",
  line: "#e2e8f0",
};

// Todo el estado de la pestaña vive en la URL: recargar o compartir el enlace
// deja la misma vista. "p_page" es el parámetro de página de TablaPostulaciones.
const PF_ID_PARAM = "pf";
const TABLE_PAGE_PARAM = "p_page";

type EditorState = { prefilter: IPrefilter | null; asCopy: boolean } | null;

const PanelPrefiltrado = () => {
  const history = useHistory();
  const location = useLocation();

  const [summary, setSummary] = useState<IPrefilterSummaryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState<number>(0);
  const [editor, setEditor] = useState<EditorState>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchPrefilterSummary(controller.signal)
      .then((data) => {
        setSummary(data);
        setError(null);
      })
      .catch((err: Error) => {
        if (controller.signal.aborted) return;
        setError(err.message || "No se pudo cargar el prefiltrado.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  // Solo se lista el grupo "Cumplen"; los demás (sin cargo, incompletos, no
  // cumplen) los sigue calculando el backend pero no se muestran.
  const prefilterId = useMemo(() => new URLSearchParams(location.search).get(PF_ID_PARAM), [location.search]);
  const selection = useMemo<IPrefilterSelection | null>(
    () => (prefilterId ? { prefilterId, bucket: "CUMPLE" } : null),
    [prefilterId],
  );

  const select = useCallback((next: string) => {
    const sp = new URLSearchParams(location.search);
    sp.set(PF_ID_PARAM, next);
    sp.delete("pfg"); // grupo de la versión anterior
    sp.set(TABLE_PAGE_PARAM, "1");
    history.replace({ pathname: location.pathname, search: `?${sp.toString()}` });
  }, [history, location.pathname, location.search]);

  const canEdit = summary?.canEdit === true;
  const groups = useMemo(() => groupByClient(summary?.prefilters ?? []), [summary]);
  const selectedPrefilter = useMemo(
    () => summary?.prefilters.find((p) => p.prefilterId === prefilterId) ?? null,
    [summary, prefilterId],
  );

  return (
    <div style={{ width: "100%", fontFamily: FONT, color: C.ink, padding: "4px 0 24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {loading && !summary ? (
          <SkeletonTheme baseColor="#eef2f7" highlightColor="#f8fafc">
            <div style={{ display: "flex", gap: 8 }}>
              {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} width={120} height={36} borderRadius={999} />)}
            </div>
          </SkeletonTheme>
        ) : (
          groups.map((group) => (
            <BotonCliente
              key={group.clientId}
              group={group}
              selectedId={prefilterId}
              onPick={(prefilter) => select(prefilter.prefilterId)}
            />
          ))
        )}
        {canEdit && (
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            {selectedPrefilter && (
              <>
                <button
                  type="button"
                  onClick={() => setEditor({ prefilter: selectedPrefilter, asCopy: false })}
                  style={secondaryButton}
                  title={`Editar ${selectedPrefilter.client ?? ""} · ${selectedPrefilter.name}`}
                >
                  <EditOutlinedIcon style={{ fontSize: 16 }} /> Editar
                </button>
                <button
                  type="button"
                  onClick={() => setEditor({ prefilter: selectedPrefilter, asCopy: true })}
                  style={secondaryButton}
                  title="Duplicar para otra sede o cliente"
                >
                  <ContentCopyIcon style={{ fontSize: 15 }} /> Duplicar
                </button>
              </>
            )}
            <button type="button" onClick={() => setEditor({ prefilter: null, asCopy: false })} style={primaryButton}>
              <AddIcon style={{ fontSize: 16 }} /> Nuevo prefiltro
            </button>
          </div>
        )}
      </div>

      {error && (
        <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: C.redBg, color: C.red, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {selection ? (
        // La tabla compara la selección por valor: un objeto nuevo no la recarga.
        <TablaPostulaciones activeTab="postulaciones" prefilter={selection} />
      ) : summary && !error && (
        <div style={{ padding: 32, textAlign: "center", color: C.slate, fontSize: 14 }}>
          {groups.length === 0
            ? `Todavía no hay prefiltros.${canEdit ? " Crea el primero con «Nuevo prefiltro»." : ""}`
            : "Elige un cliente para ver a sus postulantes prefiltrados."}
        </div>
      )}

      {editor && (
        <EditorPrefiltro
          prefilter={editor.prefilter}
          asCopy={editor.asCopy}
          knownClients={groups.map((group) => ({ value: group.clientId, label: group.client }))}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
};

// ─── botón de cliente (con menú de sedes si tiene varias) ──────────────────────

interface BotonClienteProps {
  group: IClientGroup;
  selectedId: string | null;
  onPick: (prefilter: IPrefilter) => void;
}

const BotonCliente = ({ group, selectedId, onPick }: BotonClienteProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const single = group.prefilters.length === 1;
  const selected = group.prefilters.find((p) => p.prefilterId === selectedId) ?? null;

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const pick = (prefilter: IPrefilter) => {
    setOpen(false);
    onPick(prefilter);
  };

  // Detalle al pasar el mouse: el de la sede única o el de la sede elegida. Si
  // aún no hay sede elegida no se muestra nada: el detalle está en cada sede del menú.
  const shown = single ? group.prefilters[0] : selected;
  const detail = shown ? <TarjetaPrefiltro prefilter={shown} /> : null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <DetallePrefiltro content={detail} disabled={open}>
      <button
        type="button"
        onClick={() => (single ? pick(group.prefilters[0]) : setOpen((v) => !v))}
        aria-haspopup={single ? undefined : "menu"}
        aria-expanded={single ? undefined : open}
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: single ? "8px 16px" : "8px 10px 8px 16px", borderRadius: 999,
          border: `1px solid ${selected ? C.blue : C.line}`,
          background: selected ? C.blue : "#fff",
          color: selected ? "#fff" : C.ink,
          fontFamily: FONT, fontSize: 13.5, fontWeight: 600, cursor: "pointer",
        }}
      >
        {group.client}
        {/* Sin cabecera, la sede elegida se ve en el mismo botón. */}
        {selected && !single && <span style={{ fontWeight: 400 }}>· {selected.name}</span>}
        {!single && <ExpandMoreIcon style={{ fontSize: 18, transform: open ? "rotate(180deg)" : undefined, transition: "transform .15s" }} />}
      </button>
      </DetallePrefiltro>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20, minWidth: 220,
            padding: 6, borderRadius: 10, background: "#fff", border: `1px solid ${C.line}`,
            boxShadow: "0 10px 25px rgba(15,23,42,0.12)",
          }}
        >
          {group.prefilters.map((prefilter) => {
            const current = prefilter.prefilterId === selectedId;
            return (
              <DetallePrefiltro key={prefilter.prefilterId} content={<TarjetaPrefiltro prefilter={prefilter} />} placement="right-start">
              <button
                type="button"
                role="menuitem"
                onClick={() => pick(prefilter)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, width: "100%",
                  padding: "8px 10px", borderRadius: 7, border: "none", textAlign: "left",
                  background: current ? C.blueBg : "transparent", color: current ? C.blue : C.ink,
                  fontFamily: FONT, fontSize: 13, fontWeight: current ? 600 : 400, cursor: "pointer",
                  opacity: prefilter.active ? 1 : 0.6,
                }}
              >
                <span>{prefilter.name}</span>
                {/* Los no vigentes solo los reciben quienes pueden editar. */}
                <span style={{ fontSize: 11.5, color: C.slate, whiteSpace: "nowrap" }}>
                  {prefilter.active
                    ? (prefilter.counts ? `${prefilter.counts.meets.toLocaleString("es-CL")} cumplen` : "")
                    : "No vigente"}
                </span>
              </button>
              </DetallePrefiltro>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── estilos ───────────────────────────────────────────────────────────────────

const primaryButton: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "7px 14px", borderRadius: 8, border: "none",
  background: C.blue, color: "#fff", fontFamily: FONT, fontSize: 13, fontWeight: 500, cursor: "pointer",
};

const secondaryButton: React.CSSProperties = {
  ...primaryButton,
  background: "#fff", color: "#334155", border: `1px solid ${C.line}`,
};

export default PanelPrefiltrado;
